import PropTypes from "prop-types";
import React, { memo } from "react";
import { areEqual } from "react-window";

import styled from "styled-components/macro";

import { useCellMeasurements } from "Boards";

import {
  Cell,
  DynamicCell,
  NameCell,
  TotalCell,
  TotalColumnCell,
} from "./Cells";
import { getCanEditCell } from "./Utils/getCanEditCell";
import { NAME_ACCESSOR } from "./const";

export const Row = memo(({ data, index, style }) => {
  const {
    rows,
    columns,
    saveCellValue,
    rowWidth,
    numRowsRenderedPerTick,
    shouldHideTotalColumn,
    wrapperWidth,
  } = data;
  const row = rows[index];

  const showSpacerCell = rowWidth <= wrapperWidth;

  const { width: totalColWidth } = useCellMeasurements({
    columnID: "totals",
  });

  const { entityID, entityType, name, total, relatedEntity } = row;

  const getPrimaryUserName = () => {
    if (entityType !== "signupAdditionalName") return null;
    return rows.find((r) => r.entityID === row.relatedEntity.entityID)?.name;
  };

  // removing the 100% width here b/c it causes the name column to not stick on particularly wide boards
  const { width, ...rowWrapperStyle } = style;

  return (
    <RowWrapper
      style={rowWrapperStyle}
      key={`row_${entityID}`}
      $rowWidth={rowWidth}
    >
      {columns.map(
        ({
          accessor,
          ID,
          typeDictID,
          canEdit,
          extendedFieldData,
          fieldTypeInstanceStructureGroupingID,
        }) => {
          const canEditColumn = canEdit;
          const canEditCell = getCanEditCell({
            entityType,
            typeDictID,
          });
          const canEditFinal = canEditColumn && canEditCell;

          return accessor === NAME_ACCESSOR ? (
            <NameCell
              key={index}
              entityID={entityID}
              entityType={entityType}
              name={name}
              primaryUserName={getPrimaryUserName()}
              relatedEntity={relatedEntity}
            />
          ) : (
            <Cell key={`${index}_${ID}`} columnID={ID} columnType={typeDictID}>
              <DynamicCell
                columnType={typeDictID}
                columnID={ID}
                value={row[accessor]}
                entityID={entityID}
                entityType={entityType}
                saveCellValue={saveCellValue}
                canEdit={canEditFinal}
                isInitiallyRendered={index < numRowsRenderedPerTick}
                extendedFieldData={extendedFieldData}
                fieldTypeInstanceStructureGroupingID={
                  fieldTypeInstanceStructureGroupingID
                }
              />
            </Cell>
          );
        }
      )}
      {!shouldHideTotalColumn && (
        <>
          {showSpacerCell && <SpacerCell />}
          <TotalColumnCell key={index} style={{ width: totalColWidth }}>
            <TotalCell data={total} />
          </TotalColumnCell>
        </>
      )}
    </RowWrapper>
  );
}, areEqual);

Row.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  style: PropTypes.object,
  index: PropTypes.number,
};

const RowWrapper = styled.div`
  position: relative;
  display: flex;
  width: ${({ $rowWidth }) => $rowWidth};
  min-width: 100%;
  box-sizing: border-box;
  padding: 0 12px;
`;

const SpacerCell = styled(Cell)`
  flex-grow: 1;
  flex-shrink: 1;
  width: 1px;
  min-width: 0;
  max-width: none;
`;
